/**
 * Two distinct scores per contact:
 *   - healthScore (0–100): activation + engagement along the pre-order funnel.
 *   - leadScore (0–100): ICP fit from profile/onboarding fields + LiDAR device.
 */
import {
  normalizeLifecycleStage,
  activationProgressPct,
  contactHasLidarDevice,
} from "./lifecycleStages";

function clamp(n, lo, hi) {
  return Math.max(lo, Math.min(hi, n));
}

function toMs(v) {
  if (v == null) return 0;
  if (typeof v.toMillis === "function") return v.toMillis();
  if (v instanceof Date) return v.getTime();
  if (typeof v === "object" && typeof v.seconds === "number") return v.seconds * 1000;
  if (typeof v === "number" && v > 1e11) return v;
  const t = new Date(v).getTime();
  return Number.isNaN(t) ? 0 : t;
}

function lastTouchMs(data) {
  return toMs(
    data.lastActiveAt || data.lastLoginAt || data.lastSeenAt ||
    data.lastSignInTime || data.updatedAt || data.updated_at
  );
}

function budgetLeadPoints(budget) {
  const b = String(budget || "").toLowerCase();
  if (!b) return 0;
  if (b.includes("2m") || b === "2m_plus") return 12;
  if (b.includes("500k")) return 9;
  if (b.includes("100k")) return 6;
  if (b.includes("25k")) return 3;
  return 2;
}

/**
 * Engagement / activation health (pre-order funnel + recency).
 */
export function deriveContactHealthScore(data) {
  if (!data || typeof data !== "object") return 50;
  if (data.healthScoreManual === true && typeof data.healthScore === "number") {
    return clamp(Math.round(data.healthScore), 0, 100);
  }

  let score = 35;
  const stage = normalizeLifecycleStage(data.stage);

  score += Math.round(activationProgressPct(stage) * 0.45);

  const lastMs = lastTouchMs(data);
  const createdMs = toMs(data.createdAt || data.created_at);
  const refMs = lastMs > 0 ? lastMs : createdMs;
  if (refMs > 0) {
    const days = (Date.now() - refMs) / 86400000;
    if (days < 2) score += 22;
    else if (days < 7) score += 18;
    else if (days < 14) score += 12;
    else if (days < 30) score += 6;
    else if (days < 60) score += 0;
    else if (days < 90) score -= 8;
    else score -= 16;
  }

  const fails = Number(data.paymentFailCount) || 0;
  score -= Math.min(24, fails * 8);

  const scans = Number(data.totalScans) || 0;
  score += Math.min(10, Math.log2(scans + 1) * 2.5);

  if (stage === "not_a_lead") score -= 10;

  return clamp(Math.round(score), 0, 100);
}

/**
 * ICP / profile fit for prioritizing outreach (not SaaS plan tier).
 */
export function deriveContactLeadScore(data) {
  if (!data || typeof data !== "object") return 25;
  if (data.leadScoreManual === true && typeof data.leadScore === "number") {
    return clamp(Math.round(data.leadScore), 0, 100);
  }

  let score = 10;

  if (data.workRole && String(data.workRole).trim()) score += 10;
  if (data.companyName && String(data.companyName).trim()) score += 8;
  const projectTypes = Array.isArray(data.projectTypes) ? data.projectTypes : [];
  if (projectTypes.length) score += Math.min(10, projectTypes.length * 4);
  if (data.preferredFormat && String(data.preferredFormat).trim()) score += 6;
  score += budgetLeadPoints(data.typicalProjectBudget);
  if (data.teamSize && String(data.teamSize).trim()) score += 5;
  if (data.location && String(data.location).trim()) score += 4;
  if (data.asBuildsPerYear && String(data.asBuildsPerYear).trim()) score += 6;

  const lidar = contactHasLidarDevice(data);
  if (lidar === true) score += 14;
  if (lidar === false) score -= 10;

  const ltv = Number(data.lifetimeRevenue) || 0;
  score += Math.min(12, Math.log2(ltv + 1) * 1.2);
  const stage = normalizeLifecycleStage(data.stage);
  if (stage === "paid_user") score += 10;
  else if (stage === "trial_order") score += 5;
  else if (stage === "uploaded_scans") score += 4;

  const lastMs = lastTouchMs(data);
  if (lastMs > 0) {
    const days = (Date.now() - lastMs) / 86400000;
    if (days < 7) score += 8;
    else if (days < 30) score += 4;
    else if (days > 120) score -= 6;
  }

  const fails = Number(data.paymentFailCount) || 0;
  if (fails > 0) score -= Math.min(12, fails * 5);

  return clamp(Math.round(score), 0, 100);
}

export function classifyRevenueSignals(contact) {
  if (!contact) return { isUpsellReady: false, isChurnRisk: false, isWinBackTarget: false };
  const stage = normalizeLifecycleStage(contact.stage);
  const fails = Number(contact.paymentFailCount) || 0;
  const health = Number(contact.healthScore) || 0;
  const ltv = Number(contact.lifetimeRevenue) || 0;

  const isUpsellReady = stage === "paid_user" && fails === 0 && ltv >= 500;
  const isChurnRisk = stage === "paid_user" && (health < 45 || fails >= 1);
  const isWinBackTarget = ltv > 0 && stage !== "paid_user" && stage === "uploaded_scans";

  return { isUpsellReady, isChurnRisk, isWinBackTarget };
}

export function winBackPriority(contact) {
  if (!contact) return 0;
  const ltv = Number(contact.lifetimeRevenue) || 0;
  const lastMs = lastTouchMs(contact) || toMs(contact.updatedAt);
  if (!lastMs || ltv <= 0) return ltv;
  const monthsSince = Math.max(0, (Date.now() - lastMs) / (86400000 * 30));
  const decay = Math.pow(0.5, monthsSince / 6);
  return Math.round(ltv * decay);
}
